import dayjs from "dayjs";
import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { FormControl } from "./formControl";
import { Formik, Form, useFormikContext } from "formik";
import { ChangeEvent, useContext, useState } from "react";
import authContext from "jwt_context&axios/authContext";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import {
  FormLabel,
  FormValue,
  FormValueContainer,
} from "./FilingReview/ReviewFormControl";
import {
  useGetIRSFieldDetailsQuery,
  IRSField as IRSFieldType,
  IRSSection as IRSSectionType,
  IRSDetailsResponse,
  useSubmitToIRSMutation,
} from "store/apis/IRSDetails";
import { FormikForm } from "components/FormikForm/FormikForm";
import { formatDate } from "utils/formatDate";
import { useRoleBasedView } from "hooks/useRoleBasedView";

type IRSFieldProps = {
  field: IRSFieldType;
  authtoken: Record<string, unknown>;
  groupId: string;
};

type IRSSectionProps = {
  section: IRSSectionType;
  authtoken: Record<string, unknown>;
  groupId: string;
};

type IRSSubmitModalProps = {
  open: boolean;
  onClose: () => void;
  taskId: string;
};

const IRSField = ({ field, authtoken, groupId }: IRSFieldProps) => {
  const { setFieldValue } = useFormikContext();
  if (field.editable) {
    return (
      <div>
        <FormControl
          authtoken={authtoken}
          groupId={groupId}
          type={field.type}
          id={field.field_key}
          name={field.field_key}
          label={field.field_label}
          placeholder={field.placeholder}
          options={field.options as any}
          tooltip={field.tooltip}
          defaultValue={field.value}
          mandatory={field.required}
          field={field}
          descript={field.description}
          tag={field.tag}
          document={field.document}
          isArchived={false}
          sectionKey=""
          formId=""
          updatedTask={null}
          section={null}
          formData={null}
          entityId=""
          fields={[]}
          season=""
          quarter=""
          deadline=""
          current={null}
          data={null}
          uuid=""
          autofillKey={field.autofill_key}
          autofillPrefix={field.autofill_prefix}
          autofillSuffix=""
          selectedCurrency=""
          entityName=""
          onChange={(e: any) =>
            setFieldValue(field.field_key, e.target.checked)
          }
        />
      </div>
    );
  }

  const booleanMap: Record<string, string> = { Y: "Yes", N: "No" };

  let formattedValue = field.value;
  if (field.type === "date" && dayjs(field.value).isValid()) {
    formattedValue = formatDate(field.value);
  } else if (field.value && field.value in booleanMap) {
    formattedValue = booleanMap[field.value];
  }

  return (
    <FormValueContainer>
      <FormLabel label={field.field_label} />
      <FormValue value={formattedValue} />
    </FormValueContainer>
  );
};

const IRSSection = ({ section, authtoken, groupId }: IRSSectionProps) => (
  <div className="t-space-y-4">
    <div className="t-text-subtitle-sm t-text-text-60 t-font-medium">
      {section.title}
    </div>
    <div className="t-grid t-grid-cols-2 t-gap-4">
      {section.fields.map((field) => (
        <div
          className={classNames({
            "t-col-span-2": field.type === "checkbox",
          })}
          key={field.field_key}
        >
          <IRSField field={field} authtoken={authtoken} groupId={groupId} />
        </div>
      ))}
    </div>
  </div>
);

const getInitialValues = (data: IRSDetailsResponse) => {
  const values: Record<string, string> = {};
  data.sections.forEach((section) => {
    section.fields.forEach((field) => {
      values[field.field_key] = field.value ?? "";
    });
  });
  return values;
};

const buildPayload = (
  data: IRSDetailsResponse,
  formValues: Record<string, string>
): IRSSectionType[] =>
  data.sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) => ({
      ...field,
      value: formValues[field.field_key] ?? field.value,
    })),
  }));

export const IRSSubmitModal = ({
  open,
  onClose,
  taskId,
}: IRSSubmitModalProps) => {
  const { isAdmin } = useRoleBasedView();
  const { authtoken } = useContext(authContext);
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [isFilingReviewed, setIsFilingReviewed] = useState(false);
  const { data, isLoading, isSuccess } = useGetIRSFieldDetailsQuery(
    { taskId },
    { skip: !taskId || !isAdmin }
  );
  const irsData = data ?? { sections: [] };
  const [submitToIRS, { isLoading: isSubmitting }] = useSubmitToIRSMutation();

  const closeModal = () => {
    setIsFilingReviewed(false);
    onClose();
  };

  const onSubmit = async (formValues: Record<string, string>) => {
    try {
      await submitToIRS({
        taskId,
        sections: buildPayload(irsData, formValues),
      }).unwrap();
      successToast({ message: "Filing submitted to IRS successfully" });
      closeModal();
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={closeModal}>
      <Modal.Content asChild size="large">
        <FormikForm
          enableReinitialize
          initialValues={getInitialValues(irsData)}
          onSubmit={onSubmit}
        >
          {({ submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Review & Submit to IRS</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex-1 t-min-h-0">
                <Async.Root
                  isLoading={isLoading}
                  isSuccess={isSuccess}
                  isEmpty={irsData.sections.length === 0}
                >
                  <Async.Success>
                    <div className="t-space-y-6">
                      {irsData.sections.map((section) => (
                        <IRSSection
                          key={section.title}
                          section={section}
                          authtoken={authtoken}
                          groupId={groupId}
                        />
                      ))}
                    </div>
                  </Async.Success>
                  <Async.Empty>
                    <div />
                  </Async.Empty>
                </Async.Root>
              </Modal.Body>
              <Modal.Footer>
                <div className="t-pb-3 t-border t-border-solid t-border-neutral-0 t-border-t-0 t-border-l-0 t-border-r-0 t-mb-3 t-ml-2">
                  <Checkbox
                    name="irsFilingReviewed"
                    checked={isFilingReviewed}
                    alignTop
                    label={
                      <div className="t-text-body-sm t-text-text-100 t-flex t-flex-col">
                        <div className="t-mt-1">
                          I confirm that all submitted information and documents
                          are complete, up to date, and accurate.
                        </div>
                        <div
                          className={classNames("t-text-red t-font-medium", {
                            "t-hidden": !isFilingReviewed,
                            "t-block": isFilingReviewed,
                          })}
                        >
                          This action cannot be undone.
                        </div>
                      </div>
                    }
                    onChange={(e) => setIsFilingReviewed(e.target.checked)}
                  />
                </div>
                <div className="t-flex t-justify-end t-gap-3">
                  <Button onClick={closeModal} type="button">
                    Cancel
                  </Button>
                  <ConditionalToolTip
                    condition={
                      !isFilingReviewed && "Please tick the checkbox to proceed"
                    }
                  >
                    <div>
                      <Button
                        type="submit"
                        customType="primary"
                        disabled={!isFilingReviewed || isSubmitting}
                        onClick={submitForm}
                        isLoading={isSubmitting}
                      >
                        Submit to IRS
                      </Button>
                    </div>
                  </ConditionalToolTip>
                </div>
              </Modal.Footer>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
